import { autoGradeSubmission } from '../services/autoGradingService';

// Test data for auto-grading
export const testQuestions = [
  {
    id: 'q1',
    type: 'mcq' as const,
    text: 'What is the capital of France?',
    choices: [
      { id: 'a', text: 'London', isCorrect: false },
      { id: 'b', text: 'Paris', isCorrect: true },
      { id: 'c', text: 'Berlin', isCorrect: false },
      { id: 'd', text: 'Madrid', isCorrect: false }
    ],
    points: 2
  },
  {
    id: 'q2',
    type: 'true_false' as const,
    text: 'The Earth is round.',
    correctAnswer: true,
    points: 1
  },
  {
    id: 'q3',
    type: 'multiple_select' as const,
    text: 'Which of these are programming languages?',
    choices: [
      { id: 'a', text: 'JavaScript', isCorrect: true },
      { id: 'b', text: 'Python', isCorrect: true },
      { id: 'c', text: 'HTML', isCorrect: false },
      { id: 'd', text: 'Java', isCorrect: true }
    ],
    correctAnswers: ['a', 'b', 'd'],
    points: 3
  },
  {
    id: 'q4',
    type: 'short' as const,
    text: 'What is the formula for photosynthesis?',
    correctAnswer: '6CO2 + 6H2O → C6H12O6 + 6O2',
    points: 2
  }
];

export const testStudentAnswers = {
  // Perfect answers
  perfectAnswers: {
    q1: 'b', // Correct - Paris
    q2: true, // Correct - Earth is round
    q3: ['a', 'b', 'd'], // Correct - JavaScript, Python, Java
    q4: '6CO2 + 6H2O → C6H12O6 + 6O2' // Exact match
  },

  // Mixed answers (some right, some wrong)
  mixedAnswers: {
    q1: 'a', // Wrong - London
    q2: true, // Correct - Earth is round
    q3: ['a', 'b', 'c'], // Partial - missed Java, included HTML
    q4: 'photosynthesis creates glucose from carbon dioxide and water' // Similar but not exact
  },

  // Poor answers
  poorAnswers: {
    q1: 'c', // Wrong - Berlin
    q2: false, // Wrong - Earth is not round
    q3: ['c'], // Wrong - only HTML
    q4: 'I dont know' // Completely wrong
  }
};

/**
 * Test auto-grading with different answer sets
 */
export async function testAutoGrading() {
  console.log('🧪 Testing Auto-Grading System...');

  const testCases = [
    { name: 'Perfect Student', answers: testStudentAnswers.perfectAnswers },
    { name: 'Mixed Performance', answers: testStudentAnswers.mixedAnswers },
    { name: 'Poor Performance', answers: testStudentAnswers.poorAnswers }
  ];

  for (const testCase of testCases) {
    console.log(`\n📊 Testing: ${testCase.name}`);

    const submission = {
      assignmentId: 'test-assignment',
      studentId: 'test-student',
      answers: testCase.answers,
      submittedAt: new Date(),
      questions: testQuestions
    };

    try {
      const result = await autoGradeSubmission(submission);

      console.log(`✅ Results for ${testCase.name}:`);
      console.log(`   Score: ${result.score}/${result.totalPossible} (${result.percentage}%)`);
      console.log(`   Confidence: ${result.confidence}`);
      console.log(`   Needs Review: ${result.needsManualReview.length} questions`);

      // Show detailed breakdown
      result.gradedQuestions.forEach((q, i) => {
        const status = q.isCorrect ? '✅' : '❌';
        const review = q.needsReview ? ' [REVIEW]' : '';
        console.log(`   Q${i+1}: ${status} ${q.pointsAwarded}/${q.totalPoints} pts (${q.confidence})${review}`);
      });

    } catch (error) {
      console.error(`❌ Test failed for ${testCase.name}:`, error);
    }
  }

  console.log('\n🏁 Auto-grading tests complete!');
}

/**
 * Quick test for a single question type
 */
export async function testSingleQuestionType(type: 'mcq' | 'true_false' | 'multiple_select' | 'short') {
  const question = testQuestions.find(q => q.type === type);
  if (!question) {
    console.error(`Question type ${type} not found in test data`);
    return;
  }

  const testAnswer = testStudentAnswers.perfectAnswers[question.id as keyof typeof testStudentAnswers.perfectAnswers];

  const submission = {
    assignmentId: 'single-test',
    studentId: 'test-student',
    answers: { [question.id]: testAnswer },
    submittedAt: new Date(),
    questions: [question]
  };

  const result = await autoGradeSubmission(submission);
  console.log(`🎯 ${type.toUpperCase()} Test Result:`, result);

  return result;
}

/**
 * Test PDF.js worker loading
 */
export async function testPDFWorker() {
  console.log('🔧 Testing PDF.js worker...');

  try {
    // Import PDF.js
    const pdfjsLib = await import('pdfjs-dist');

    // Check if worker is configured
    console.log('📄 PDF.js worker source:', pdfjsLib.GlobalWorkerOptions.workerSrc);

    // Create a simple test PDF (minimal PDF structure)
    const testPDFData = new Uint8Array([
      0x25, 0x50, 0x44, 0x46, 0x2D, 0x31, 0x2E, 0x34, 0x0A, 0x25, 0xC7, 0xEC, 0x8F, 0xA2, 0x0A,
      0x31, 0x20, 0x30, 0x20, 0x6F, 0x62, 0x6A, 0x0A, 0x3C, 0x3C, 0x0A, 0x2F, 0x54, 0x79, 0x70,
      0x65, 0x20, 0x2F, 0x43, 0x61, 0x74, 0x61, 0x6C, 0x6F, 0x67, 0x0A, 0x2F, 0x50, 0x61, 0x67,
      0x65, 0x73, 0x20, 0x32, 0x20, 0x30, 0x20, 0x52, 0x0A, 0x3E, 0x3E, 0x0A, 0x65, 0x6E, 0x64,
      0x6F, 0x62, 0x6A, 0x0A
    ]);

    // Test loading the PDF
    const loadingTask = pdfjsLib.getDocument({ data: testPDFData });
    const pdf = await loadingTask.promise;

    console.log('✅ PDF.js worker loaded successfully!');
    console.log(`📊 Test PDF has ${pdf.numPages} page(s)`);

    return { success: true, workerSrc: pdfjsLib.GlobalWorkerOptions.workerSrc };

  } catch (error) {
    console.error('❌ PDF.js worker test failed:', error);
    return { success: false, error: error.message };
  }
}

// Add to window for easy testing in browser console
if (typeof window !== 'undefined') {
  (window as any).testAutoGrading = testAutoGrading;
  (window as any).testSingleQuestionType = testSingleQuestionType;
  (window as any).testPDFWorker = testPDFWorker;
}